"use client";

import ExternalLinkModal from "@/components/ExternalLinkModal/ExternalLinkModal";
import { useState, useEffect } from "react";
import { useTranslations, useLocale } from "next-intl";
import { WhatToDo } from "@/lib/api/datosGenerales/types/whatToDo";
import { fetchWhatToDo } from "@/lib/api/datosGenerales/fetchWhatToDo";
import SectionShell from "@/ui/elements/sectionShell";
import MediaCard from "@/ui/elements/mediaCard";
import Reveal from "@/ui/elements/reveal";
import { type Locale } from "@/i18n/config";

export default function ImageCardsSection() {
  const [data, setData] = useState<WhatToDo[]>([]);
  const locale = useLocale() as Locale;
  const [loading, setLoading] = useState(true);
  const t = useTranslations("whatToDo");
  const [selectedUrl, setSelectedUrl] = useState<string | null>(null);

  const handleContinue = () => {
    if (selectedUrl) {
      window.open(selectedUrl, "_blank");
      setSelectedUrl(null);
    }
  };

  useEffect(() => {
    async function loadData() {
      try {
        const whatToDo = await fetchWhatToDo(locale);
        setData(whatToDo);
      } catch (error) {
        console.error("Error al cargar los datos de Qué hacer:", error);
      } finally {
        setLoading(false);
      }
    }
    loadData();
  }, [locale]);

  return (
    <>
      <SectionShell
        eyebrow={t("eyebrow")}
        title={t("title")}
        subtitle={t("desc")}
        tone="muted"
      >
        <Reveal className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
          {loading
            ? Array.from({ length: 3 }).map((_, i) => (
                <div
                  key={i}
                  className="rounded-2xl overflow-hidden bg-[var(--blanco)] ring-1 ring-black/5 shadow-lg"
                >
                  <div className="aspect-[16/10] bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 animate-pulse relative overflow-hidden">
                    <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/40 to-transparent animate-shimmer"></div>
                  </div>
                  <div className="p-6 flex flex-col gap-3">
                    <div className="h-5 w-3/4 rounded bg-gray-200 animate-pulse"></div>
                    <div className="h-4 w-full rounded bg-gray-200 animate-pulse"></div>
                  </div>
                </div>
              ))
            : data.map((item, index) => (
                <MediaCard
                  key={item.id}
                  image={item.imagen}
                  title={item.nombre}
                  actionLabel={t("viewMore")}
                  onAction={() => item.url && setSelectedUrl(item.url)}
                  priority={index === 0}
                />
              ))}
        </Reveal>
      </SectionShell>

      <ExternalLinkModal
        isOpen={!!selectedUrl}
        onClose={() => setSelectedUrl(null)}
        onContinue={handleContinue}
      />
    </>
  );
}
